import {
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  IconButton,
  Popover,
  InputAdornment,
} from "@mui/material";
import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { MenuItem } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs, { Dayjs } from "dayjs";
import * as Yup from "yup";
import CancelIcon from "@mui/icons-material/Cancel";
import { SA_ORG_UPDATE } from "../../shared/constants/apiEndpoints";
import { makeEncryptedApiCall } from "../../utils/encryptionUtils";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

interface OrganizationDetails {
  organization_name?: string;
  org_name?: string;
  organization_address?: string;
  city?: string;
  country?: string;
  state?: string;
  pin_code?: string;
  admin_first_name?: string;
  admin_last_name?: string;
  admin_email?: string;
  organization_email?: string;
  admin_contact?: string;
  lead_source?: string;
  lead_source_contact?: string;
  lead_source_email?: string;
  organization_website?: string;
  account_manager?: string;
  account_manager_email?: string;
  account_manager_contact?: string;
  subscription_type?: string;
  deployment_type?: string;
  inventory_type?: string;
  on_prem_assets?: number | string;
  cloud_resources?: number | string;
  validity_days?: number | string;
  subscription_details?: {
    subscription_type?: string;
    deployment_type?: string;
    inventory_type?: string;
    on_prem_assets?: number | string;
    cloud_resources?: number | string;
    validity_days?: number | string;
    expiry_date?: string;
  };
}

interface InviteUpdateDrawerProps {
  isPreviewDetails: OrganizationDetails;
  onCloseDrawer?: () => void;
  isSelectedRow: any;
  setIsUpdateButtonClicked?: (value: boolean) => void;
  setIsTableRowClick?: (value: boolean) => void;
  onOrgUpdate?: () => void;
}

const InviteUpdateDrawer: React.FC<InviteUpdateDrawerProps> = ({
  isPreviewDetails,
  onCloseDrawer: _onCloseDrawer,
  isSelectedRow,
  setIsUpdateButtonClicked,
  setIsTableRowClick,
  onOrgUpdate,
}) => {
  console.log(isPreviewDetails, "isPreviewDetails");
  const subscriptionType = ["Free Trial", "Free Custom", "Paid Custom"];
  const deploymentType = ["On-prem", "SaaS"];
  // const deploymentType = ["SaaS"];
  const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(),
    dayjs().add(13, "day"),]);
  const inventoryType = [
    "On-prem & Cloud",
    "On-prem Assets",
    "Cloud Resources",
  ];

  const [fieldState, setFieldState] = React.useState<{
    onPremAssets: { disabled: boolean; min: number; max: number | undefined; value: string };
    cloudResources: { disabled: boolean; min: number; max: number | undefined; value: string };
    validity: { disabled: boolean; min: number; max: number | undefined; value: string };
    validityOptions: number[];
  }>({
    onPremAssets: { disabled: false, min: 30, max: undefined, value: "" },
    cloudResources: { disabled: false, min: 30, max: undefined, value: "" },
    validity: { disabled: false, min: 30, max: 365, value: "" },
    validityOptions: [13, 30, 60, 90, 180, 365],
  });
  const validation_schema = Yup.object().shape({
    name: Yup.string()
      .max(256, "Name contain maximum 256 characters")
      .required(" Name is required"),
    address: Yup.string()
      .required("Address is required")
      .max(64, "Address contain maximum 64 characters"),
    city: Yup.string().required("City is required"),
    country: Yup.string().required("Country is required"),
    state: Yup.string(),
    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required"),
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    contact: Yup.string()
      .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit contact number")
      .required("Contact is required"),
    leadSource: Yup.string().required("Lead Source is required"),
    leadSourceEmail: Yup.string()
      .email("Invalid email format")
      .required("Lead Source Email is required"),
    leadSourceContact: Yup.string()
      .required("Lead Source Contact is required")
      .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit contact number"),
    organisationWebsite: Yup.string()
      .url("Invalid URL format")
      .required("Organization Website is required"),
    accountManager: Yup.string(),
    accountManagerEmail: Yup.string().email("Invalid email format"),
    accountManagerContact: Yup.string(),
    subscriptionType: Yup.string().required("Subscription Type is required"),
    deploymenttype: Yup.string().required("Deployment Type is required"),
    inventoryType: Yup.string().required("Inventory Type is required"),
    onPremAssets: Yup.number()
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          (inventoryType === "On-prem & Cloud" ||
            inventoryType === "On-prem Assets"),
        then: (schema) =>
          schema
            .required("On-prem Assets is required")
            .min(30, "Minimum is 30"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Paid Custom" &&
          inventoryType === "On-prem Assets",
        then: (schema) =>
          schema
            .required("On-prem Assets is required")
            .min(30, "Minimum is 30"),
        otherwise: (schema) => schema,
      }),
    cloudResources: Yup.number()
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "On-prem & Cloud",
        then: (schema) =>
          schema
            .required("Cloud Resources is required")
            .min(30, "Minimum is 30"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "On-prem Assets",
        then: (schema) => schema.oneOf([0], "Cloud Resources must be 0"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "Cloud Resources",
        then: (schema) =>
          schema
            .required("Cloud Resources is required")
            .min(30, "Minimum is 30"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Paid Custom" &&
          inventoryType === "On-prem Assets",
        then: (schema) => schema.oneOf([0], "Cloud Resources must be 0"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Paid Custom" &&
          inventoryType === "Cloud Resources",
        then: (schema) =>
          schema
            .required("Cloud Resources is required")
            .min(30, "Minimum is 30"),
        otherwise: (schema) => schema,
      }),
    validity: Yup.number()
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "On-prem & Cloud",
        then: (schema) =>
          schema
            .required("Validity is required")
            .min(30, "Minimum is 30 days")
            .max(90, "Maximum is 90 days (3 months)"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "On-prem Assets",
        then: (schema) =>
          schema
            .required("Validity is required")
            .min(30, "Minimum is 30 days")
            .max(90, "Maximum is 90 days (3 months)"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Free Custom" &&
          inventoryType === "Cloud Resources",
        then: (schema) =>
          schema
            .required("Validity is required")
            .min(30, "Minimum is 30 days")
            .max(90, "Maximum is 90 days (3 months)"),
        otherwise: (schema) => schema,
      })
      .when(["subscriptionType", "inventoryType"], {
        is: (subscriptionType: string, inventoryType: string) =>
          subscriptionType === "Paid Custom",
        then: (schema) =>
          schema
            .required("Validity is required")
            .min(30, "Minimum is 30 days")
            .max(365, "Maximum is 365 days (12 months)"),
        otherwise: (schema) => schema,
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: isPreviewDetails?.organization_name || isPreviewDetails?.org_name || "",
      address: isPreviewDetails?.organization_address || "",
      city: isPreviewDetails?.city || "",
      country: isPreviewDetails?.country || "",
      state: isPreviewDetails?.state || "",
      pincode: isPreviewDetails?.pin_code || "",
      firstname: isPreviewDetails?.admin_first_name || "",
      lastname: isPreviewDetails?.admin_last_name || "",
      email: isPreviewDetails?.admin_email || isPreviewDetails?.organization_email || "",
      contact: isPreviewDetails?.admin_contact || "",
      leadSource: isPreviewDetails?.lead_source || "",
      leadSourceEmail: isPreviewDetails?.lead_source_email || "",
      leadSourceContact: isPreviewDetails?.lead_source_contact || "",
      organisationWebsite: isPreviewDetails?.organization_website || "",
      accountManager: isPreviewDetails?.account_manager || "",
      accountManagerEmail: isPreviewDetails?.account_manager_email || "",
      accountManagerContact: isPreviewDetails?.account_manager_contact || "",
      subscriptionType:
        isPreviewDetails?.subscription_details?.subscription_type?.trim() ||
        isPreviewDetails?.subscription_type?.trim() || "",
      deploymenttype:
        (isPreviewDetails?.subscription_details?.deployment_type?.trim() ||
          isPreviewDetails?.deployment_type?.trim() ||
          "").replace("On Prem", "On-prem"),
      inventoryType:
        (isPreviewDetails?.subscription_details?.inventory_type?.trim() ||
          isPreviewDetails?.inventory_type?.trim() ||
          "").replace("On Prem & Cloud", "On-prem & Cloud"),
      onPremAssets:
        isPreviewDetails?.subscription_details?.on_prem_assets?.toString() ||
        isPreviewDetails?.on_prem_assets?.toString() || "",
      cloudResources:
        isPreviewDetails?.subscription_details?.cloud_resources?.toString() ||
        isPreviewDetails?.cloud_resources?.toString() || "",
      validity: isPreviewDetails?.subscription_details?.validity_days?.toString() ||
        isPreviewDetails?.validity_days?.toString() || "",
    },
    validationSchema: validation_schema,
    onSubmit: async (values) => {
      try {
        // Helper function to normalize strings: trim and replace multiple spaces with single space
        const normalizeString = (str: string | undefined): string => {
          if (!str) return "";
          return str.trim().replace(/\s+/g, " ");
        };

        const payload = {
          organization_name: normalizeString(values.name),
          organization_address: normalizeString(values.address),
          organization_website: normalizeString(values.organisationWebsite),
          country: normalizeString(values.country),
          state: normalizeString(values.state),
          city: normalizeString(values.city),
          pin_code: normalizeString(values.pincode),
          firstname: normalizeString(values.firstname),
          lastname: normalizeString(values.lastname),
          organization_email: normalizeString(values.email),
          contact: normalizeString(values.contact),
          lead_source: normalizeString(values.leadSource),
          lead_source_email: normalizeString(values.leadSourceEmail),
          lead_source_contact: normalizeString(values.leadSourceContact),
          account_manager: normalizeString(values.accountManager),
          account_manager_email: normalizeString(values.accountManagerEmail),
          account_manager_contact: normalizeString(values.accountManagerContact),
          subscription_type: normalizeString(values.subscriptionType),
          deployment_type: normalizeString(values.deploymenttype),
          inventory_type: normalizeString(values.inventoryType),
          on_prem_assets: parseInt(String(values.onPremAssets)) || 0,
          cloud_resources: parseInt(String(values.cloudResources)) || 0,
          validity_days: parseInt(String(values.validity)) || 90,
          sso_enabled: false,
        };

        const response = await makeEncryptedApiCall(
          SA_ORG_UPDATE(isSelectedRow),
          payload,
          "PUT"
        );
        // const response = await axiosInstance.put(`/authentication/sa-organizations/${isSelectedRow}/update`, payload);
        toast.success(response?.data?.message);
        setIsUpdateButtonClicked?.(false);
        setIsTableRowClick?.(false);
        if (onOrgUpdate) onOrgUpdate();

        // navigate("/organisation-table");
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const isUpdatingFromDateRange = useRef(false);

  // Initialize date range based on existing validity (when component loads or isPreviewDetails changes)
  React.useEffect(() => {
    const validityDays = parseInt(formik.values.validity) || 13;
    const expiryDate = isPreviewDetails?.subscription_details?.expiry_date;

    if (expiryDate && !isUpdatingFromDateRange.current) {
      // If expiry_date is present, use it to set the dateRange
      const endDate = dayjs(expiryDate);
      if (endDate.isValid()) {
        const startDate = endDate.subtract(validityDays - 1, "day");
        setDateRange([startDate, endDate]);
        return;
      }
    }

    if (validityDays > 0 && !isUpdatingFromDateRange.current) {
      const currentDays = dateRange[0] && dateRange[1]
        ? dateRange[1].diff(dateRange[0], "day") + 1
        : 0;
      if (currentDays !== validityDays) {
        setDateRange([dayjs(), dayjs().add(validityDays - 1, "day")]);
      }
    }
    // eslint-disable-next-line
  }, [isPreviewDetails]);

  // Calculate validity days from date range
  React.useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      isUpdatingFromDateRange.current = true;
      const days = dateRange[1].diff(dateRange[0], "day") + 1;
      formik.setFieldValue("validity", days.toString());
      setTimeout(() => {
        isUpdatingFromDateRange.current = false;
      }, 0);
    }
  }, [dateRange]);

  React.useEffect(() => {
    const subType = formik.values.subscriptionType.trim();
    const invType = formik.values.inventoryType;

    // Free Trial rules
    if (subType === "Free Trial") {
      // Set inventory type to "On-prem & Cloud" for Free Trial
      if (formik.values.inventoryType !== "On-prem & Cloud") {
        formik.setFieldValue("inventoryType", "On-prem & Cloud");
      }
      // Set validity to 13 days for Free Trial
      if (formik.values.validity !== "13") {
        formik.setFieldValue("validity", "13");
      }
      // Set date range for Free Trial (13 days)
      setDateRange([dayjs(), dayjs().add(13, "day")]);
      // Set onPremAssets to 30 for Free Trial
      if (formik.values.onPremAssets !== "30") {
        formik.setFieldValue("onPremAssets", "30");
      }
      // Set cloudResources to 30 for Free Trial
      if (formik.values.cloudResources !== "50") {
        formik.setFieldValue("cloudResources", "50");
      }
      // Update fieldState for Free Trial
      setFieldState({
        onPremAssets: { disabled: true, min: 30, max: 30, value: "30" },
        cloudResources: { disabled: true, min: 50, max: 50, value: "50" },
        validity: { disabled: true, min: 13, max: 13, value: "13" },
        validityOptions: [13],
      });
    } else if (subType === "Free Custom" || subType === "Paid Custom") {
      if (invType === "On-prem & Cloud") {
        setFieldState({
          onPremAssets: {
            disabled: false,
            min: 30,
            max: undefined,
            value: formik.values.onPremAssets || "30",
          },
          cloudResources: {
            disabled: false,
            min: 30,
            max: undefined,
            value: formik.values.cloudResources || "30",
          },
          validity: {
            disabled: false,
            min: 30,
            max: subType === "Free Custom" ? 90 : 365,
            value: formik.values.validity || "30",
          },
          validityOptions:
            subType === "Free Custom"
              ? [30, 60, 90]
              : [30, 60, 90, 180, 365],
        });
      } else if (invType === "On-prem Assets") {
        setFieldState({
          onPremAssets: {
            disabled: false,
            min: 30,
            max: undefined,
            value: formik.values.onPremAssets || "30",
          },
          cloudResources: { disabled: true, min: 0, max: 0, value: "0" },
          validity: {
            disabled: false,
            min: 30,
            max: subType === "Free Custom" ? 90 : 365,
            value: formik.values.validity || "30",
          },
          validityOptions:
            subType === "Free Custom"
              ? [30, 60, 90]
              : [30, 60, 90, 180, 365],
        });
      } else if (invType === "Cloud Resources") {
        setFieldState({
          onPremAssets: { disabled: true, min: 0, max: 0, value: "0" },
          cloudResources: {
            disabled: false,
            min: 30,
            max: undefined,
            value: formik.values.cloudResources || "30",
          },
          validity: {
            disabled: false,
            min: 30,
            max: subType === "Free Custom" ? 90 : 365,
            value: formik.values.validity || "30",
          },
          validityOptions:
            subType === "Free Custom"
              ? [30, 60, 90]
              : [30, 60, 90, 180, 365],
        });
      }
    } else {
      setFieldState({
        onPremAssets: {
          disabled: false,
          min: 30,
          max: undefined,
          value: formik.values.onPremAssets || "",
        },
        cloudResources: {
          disabled: false,
          min: 30,
          max: undefined,
          value: formik.values.cloudResources || "",
        },
        validity: {
          disabled: false,
          min: 30,
          max: 365,
          value: formik.values.validity || "",
        },
        validityOptions: [13, 30, 60, 90, 180, 365],
      });
    }

    const isFreeCustomOnPremAssets =
      formik.values.subscriptionType === "Free Custom" &&
      formik.values.inventoryType === "On-prem Assets";

    if (isFreeCustomOnPremAssets) {
      if (formik.values.cloudResources !== "0") {
        formik.setFieldValue("cloudResources", "0");
      }
    }
    const isFreeCustomCloudResources =
      formik.values.subscriptionType === "Free Custom" &&
      formik.values.inventoryType === "Cloud Resources";

    if (isFreeCustomCloudResources) {
      if (formik.values.onPremAssets !== "0") {
        formik.setFieldValue("onPremAssets", "0");
      }
      // Ensure default minimum of 30 for Cloud Resources while keeping it editable
      const cloudResourcesNum = Number(formik.values.cloudResources);
      if (
        formik.values.cloudResources === "" ||
        Number.isNaN(cloudResourcesNum) ||
        cloudResourcesNum < 30
      ) {
        formik.setFieldValue("cloudResources", "30");
      }
    }

    // Paid Custom rules
    const isPaidCustomOnPremAssets =
      formik.values.subscriptionType === "Paid Custom" &&
      formik.values.inventoryType === "On-prem Assets";

    if (isPaidCustomOnPremAssets) {
      // Cloud Resources must be 0 and not editable
      if (formik.values.cloudResources !== "0") {
        formik.setFieldValue("cloudResources", "0");
      }
      // On-prem Assets must be at least 30; set default 30 if empty/invalid/less
      const onPremAssetsNum = Number(formik.values.onPremAssets);
      if (
        formik.values.onPremAssets === "" ||
        Number.isNaN(onPremAssetsNum) ||
        onPremAssetsNum < 30
      ) {
        formik.setFieldValue("onPremAssets", "30");
      }
    }

    const isPaidCustomCloudResources =
      formik.values.subscriptionType === "Paid Custom" &&
      formik.values.inventoryType === "Cloud Resources";

    if (isPaidCustomCloudResources) {
      // On-prem Assets must be 0 and not editable
      if (formik.values.onPremAssets !== "0") {
        formik.setFieldValue("onPremAssets", "0");
      }
      // Cloud Resources must be at least 30; set default 30 if empty/invalid/less
      const cloudResourcesNum2 = Number(formik.values.cloudResources);
      if (
        formik.values.cloudResources === "" ||
        Number.isNaN(cloudResourcesNum2) ||
        cloudResourcesNum2 < 30
      ) {
        formik.setFieldValue("cloudResources", "30");
      }
    }
  }, [
    formik.values.subscriptionType,
    formik.values.inventoryType,
  ]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: "bold", padding: 2, fontSize: "16px" }}>
          Update Organization
        </Typography>
        <IconButton
          style={{
            color: "#A6A5B6",
          }}
        >
          {" "}
          <CancelIcon onClick={() => setIsUpdateButtonClicked?.(false)} />
        </IconButton>
      </div>
      <Divider />
      <div style={{ padding: 20 }}>
        <Typography variant="body2" fontWeight="bold">
          Organization Information
        </Typography>

        <form onSubmit={formik.handleSubmit} style={{ flexGrow: 1 }}>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              sx={{ mt: 2 }}
              id="name"
              label="Name"
              variant="outlined"
              name="name"
              required
              fullWidth
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                formik.setFieldValue("name", e.target.value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              inputProps={{ maxLength: 64 }}
            />

            <TextField
              sx={{ mt: 2 }}
              id="address"
              label="Address 1"
              variant="outlined"
              name="address"
              required
              fullWidth
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z0-9\s]/g, ""); // alphabets + numbers + spaces
                formik.setFieldValue("address", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.address}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              inputProps={{ maxLength: 64 }}
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              id="City"
              label="City"
              variant="outlined"
              required
              fullWidth
              name="city"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allow letters, spaces, hyphen, apostrophe
                formik.setFieldValue("city", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.city}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
              inputProps={{ maxLength: 64 }}
            />

            <TextField
              id="Country"
              label="Country"
              variant="outlined"
              required
              fullWidth
              name="country"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allow letters, spaces, hyphen, apostrophe
                formik.setFieldValue("country", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.country}
              error={formik.touched.country && Boolean(formik.errors.country)}
              helperText={formik.touched.country && formik.errors.country}
              inputProps={{ maxLength: 64 }}
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              id="State"
              label="State"
              variant="outlined"
              fullWidth
              name="state"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allows only letters, spaces, hyphens, apostrophes
                formik.setFieldValue("state", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.state}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
              inputProps={{ maxLength: 64 }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              sx={{ width: "49%" }}
              id="pincode"
              label="Pincode"
              variant="outlined"
              required
              fullWidth
              name="pincode"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              error={formik.touched.pincode && Boolean(formik.errors.pincode)}
              onChange={(e) => {
                // Allow only digits
                const value = e.target.value.replace(/\D/g, "");
                formik.setFieldValue("pincode", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.pincode}
              helperText={formik.touched.pincode && formik.errors.pincode}
              inputProps={{
                inputMode: "numeric", // Mobile keyboards will show number pad
                pattern: "[0-9]*", // Enforces numeric input
                maxLength: 6, // Optional: max 6 digits
              }}
            />
          </Box>
          <Typography variant="body2" fontWeight="bold" marginTop={2}>
            Admin Information
          </Typography>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              sx={{ mt: 2 }}
              id="firstname"
              label="First Name"
              variant="outlined"
              required
              fullWidth
              name="firstname"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              error={
                formik.touched.firstname && Boolean(formik.errors.firstname)
              }
              helperText={formik.touched.firstname && formik.errors.firstname}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allow only letters, spaces, hyphens, apostrophes
                formik.setFieldValue("firstname", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.firstname}
              inputProps={{ maxLength: 64 }}
            />

            <TextField
              sx={{ mt: 2 }}
              id="lastname"
              label="Last Name"
              variant="outlined"
              required
              fullWidth
              name="lastname"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allow only letters, spaces, hyphens, apostrophes
                formik.setFieldValue("lastname", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.lastname}
              error={formik.touched.lastname && Boolean(formik.errors.lastname)}
              helperText={formik.touched.lastname && formik.errors.lastname}
              inputProps={{ maxLength: 64 }}
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              sx={{ width: "49%" }}
              id="email"
              label="Email"
              variant="outlined"
              required
              fullWidth
              name="email"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              sx={{ width: "49%" }}
              id="contact"
              label="Contact"
              variant="outlined"
              required
              fullWidth
              name="contact"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                formik.setFieldValue("contact", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.contact}
              error={formik.touched.contact && Boolean(formik.errors.contact)}
              helperText={formik.touched.contact && formik.errors.contact}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10, // for example, 10 digits max
              }}
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              id="Lead Source"
              label="Lead Source"
              variant="outlined"
              required
              fullWidth
              name="leadSource"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                // allow only letters, spaces, hyphens, apostrophes
                formik.setFieldValue("leadSource", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.leadSource}
              error={
                formik.touched.leadSource && Boolean(formik.errors.leadSource)
              }
              helperText={formik.touched.leadSource && formik.errors.leadSource}
              inputProps={{ maxLength: 64 }}
            />
            <TextField
              id="Leadmail"
              label="Lead Source Email"
              variant="outlined"
              required
              fullWidth
              name="leadSourceEmail"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.leadSourceEmail}
              error={
                formik.touched.leadSourceEmail &&
                Boolean(formik.errors.leadSourceEmail)
              }
              helperText={
                formik.touched.leadSourceEmail &&
                formik.errors.leadSourceEmail
              }
              inputProps={{ maxLength: 64 }}
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              sx={{ width: "49%" }}
              id="LeadSourceContact"
              label="Lead Source Contact"
              variant="outlined"
              required
              fullWidth
              name="leadSourceContact"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, ""); // allow only digits
                formik.setFieldValue("leadSourceContact", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.leadSourceContact}
              error={
                formik.touched.leadSourceContact &&
                Boolean(formik.errors.leadSourceContact)
              }
              helperText={
                formik.touched.leadSourceContact &&
                formik.errors.leadSourceContact
              }
              inputProps={{ maxLength: 10 }} // optional: limit to 10 digits (phone number style)
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              id="Organisation Website"
              label="Organization Website"
              variant="outlined"
              required
              fullWidth
              name="organisationWebsite"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.organisationWebsite}
              error={
                formik.touched.organisationWebsite &&
                Boolean(formik.errors.organisationWebsite)
              }
              helperText={
                formik.touched.organisationWebsite &&
                formik.errors.organisationWebsite
              }
            />
            <TextField
              id="Account Manager"
              label="Account Manager(Optional)"
              variant="outlined"
              fullWidth
              name="accountManager"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accountManager}
              error={
                formik.touched.accountManager &&
                Boolean(formik.errors.accountManager)
              }
              helperText={
                formik.touched.accountManager && formik.errors.accountManager
              }
            />
          </Box>
          <Box display="flex" gap={2} className="mb_3">
            <TextField
              id="Account Manager Email"
              label="Account Manager Email(Optional)"
              variant="outlined"
              fullWidth
              name="accountManagerEmail"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accountManagerEmail}
              error={
                formik.touched.accountManagerEmail &&
                Boolean(formik.errors.accountManagerEmail)
              }
              helperText={
                formik.touched.accountManagerEmail &&
                formik.errors.accountManagerEmail
              }
            />
            <TextField
              id="Account Manager Contact"
              label="Account Manager Contact(Optional)"
              variant="outlined"
              fullWidth
              name="accountManagerContact"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accountManagerContact}
              error={
                formik.touched.accountManagerContact &&
                Boolean(formik.errors.accountManagerContact)
              }
              helperText={
                formik.touched.accountManagerContact &&
                formik.errors.accountManagerContact
              }
            />
          </Box>
          <Typography variant="body2" fontWeight="bold" marginTop={2}>
            Subscription
          </Typography>
          <Box display="flex" gap={2} className="mb_3">
            {/* Subscription Select */}
            <TextField
              sx={{ mt: 2 }}
              id="SubscriptionType"
              select
              label="Subscription Type"
              variant="outlined"
              // required
              fullWidth
              name="subscriptionType"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.subscriptionType}
              error={
                formik.touched.subscriptionType &&
                Boolean(formik.errors.subscriptionType)
              }
              helperText={
                formik.touched.subscriptionType &&
                formik.errors.subscriptionType
              }
            >
              {subscriptionType.map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </TextField>

            {/* Deployment Select */}
            <TextField
              sx={{ mt: 2 }}
              id="DeploymentType"
              select
              label="Deployment Type"
              variant="outlined"
              required
              fullWidth
              name="deploymenttype"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.deploymenttype}
              error={
                formik.touched.deploymenttype &&
                Boolean(formik.errors.deploymenttype)
              }
              helperText={
                formik.touched.deploymenttype && formik.errors.deploymenttype
              }
            >
              {deploymentType.map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box display="flex" gap={2}>
            {/* Inventory Select */}
            <TextField
              id="Inventory type"
              select
              label="Inventory Type"
              variant="outlined"
              required
              fullWidth
              name="inventoryType"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.inventoryType}
              error={
                formik.touched.inventoryType &&
                Boolean(formik.errors.inventoryType)
              }
              helperText={
                formik.touched.inventoryType && formik.errors.inventoryType
              }
              disabled={formik.values.subscriptionType.trim() === "Free Trial"}
            >
              {inventoryType.map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="On-prem Assets"
              label="On-prem Assets"
              variant="outlined"
              required
              fullWidth
              name="onPremAssets"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // Only allow digits
                formik.setFieldValue("onPremAssets", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.onPremAssets}
              disabled={
                formik.values.subscriptionType === "Free Trial" ||
                (formik.values.subscriptionType === "Free Custom" &&
                  formik.values.inventoryType === "Cloud Resources") ||
                (formik.values.subscriptionType === "Paid Custom" &&
                  formik.values.inventoryType === "Cloud Resources")
              }
              inputProps={{
                min:
                  (formik.values.subscriptionType === "Free Custom" ||
                    formik.values.subscriptionType === "Paid Custom") &&
                    (formik.values.inventoryType === "On-prem & Cloud" ||
                      formik.values.inventoryType === "On-prem Assets")
                    ? 30
                    : undefined,
                type: "text",
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              sx={{ mt: 2 }}
              id="Cloud Resources"
              label="Cloud Resources"
              variant="outlined"
              required
              fullWidth
              name="cloudResources"
              InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // Only allow digits
                formik.setFieldValue("cloudResources", value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.cloudResources}
              disabled={
                formik.values.subscriptionType === "Free Trial" ||
                (formik.values.subscriptionType === "Free Custom" &&
                  formik.values.inventoryType === "On-prem Assets") ||
                (formik.values.subscriptionType === "Paid Custom" &&
                  formik.values.inventoryType === "On-prem Assets")
              }
              inputProps={{
                min:
                  (formik.values.subscriptionType === "Free Custom" ||
                    formik.values.subscriptionType === "Paid Custom") &&
                    (formik.values.inventoryType === "On-prem & Cloud" ||
                      formik.values.inventoryType === "Cloud Resources")
                    ? 30
                    : undefined,
                type: "text",
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
            {/* validity days */}
            <Box sx={{ width: "100%" }}>
              <TextField
                sx={{ mt: 2 }}
                id="expired-date"
                label="Expired (in days) *"
                variant="outlined"
                fullWidth
                name="expiredDate"
                InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
                value={
                  dateRange[1]
                    ? dateRange[1].format("DD/MM/YYYY")
                    : ""
                }
                onClick={(e) => {
                  if (!fieldState.validity.disabled) {
                    setDateRangeAnchor(e.currentTarget);
                  }
                }}
                disabled={fieldState.validity.disabled}
                error={formik.touched.validity && Boolean(formik.errors.validity)}
                helperText={
                  formik.touched.validity && formik.errors.validity
                    ? formik.errors.validity
                    : dateRange[0] && dateRange[1]
                      ? `${dateRange[1].diff(dayjs(), "day") + 1} days from today`
                      : formik.values.validity
                        ? `Today to end date (${formik.values.validity} days)`
                        : "Today to end date (13 days)"
                }
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!fieldState.validity.disabled) {
                            setDateRangeAnchor(e.currentTarget);
                          }
                        }}
                        edge="end"
                        disabled={fieldState.validity.disabled}
                      >
                        <CalendarTodayIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Popover
                open={Boolean(dateRangeAnchor)}
                anchorEl={dateRangeAnchor}
                onClose={() => setDateRangeAnchor(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: {
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select end date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={dateRange[1]}
                      onChange={(newValue) => {
                        if (newValue) {
                          const newRange: [Dayjs | null, Dayjs | null] = [
                            dateRange[0],
                            newValue,
                          ];
                          setDateRange(newRange);
                          if (newRange[0] && newRange[1]) {
                            const days = newRange[1].diff(newRange[0], "day");
                            formik.setFieldValue("validity", days.toString());
                            formik.setFieldTouched("validity", true);
                          }
                          setDateRangeAnchor(null);
                        }
                      }}
                      minDate={dayjs()} // Today for all subscription types (validation enforces minimums)
                      maxDate={
                        (() => {
                          const subType = formik.values.subscriptionType.trim();
                          if (subType === "Free Trial") {
                            return dayjs().add(14, "day");
                          } else if (subType === "Free Custom") {
                            return dayjs().add(90, "day"); // Max 3 months from today
                          } else if (subType === "Paid Custom") {
                            return dayjs().add(365, "day"); // Max 12 months from today
                          }
                          return undefined;
                        })()
                      }
                      disabled={fieldState.validity.disabled}
                    />
                  </LocalizationProvider>
                </Box>
              </Popover>
            </Box>
          </Box>
          <Box
            mt={3}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            {/* <Box
              sx={(theme) => ({
                display: "flex",
                height: 40,
                alignItems: "center",
                bgcolor: theme.palette.mode === "dark" ? "#181818" : "#f5f5f5",
                width: "100%",
                borderRadius: 2,
                justifyContent: "space-between",
              })}
            > */}
            {/* <Typography variant="body2" sx={{ ml: 2, fontWeight: 500 }}>
                Enable SSO
              </Typography>
              <div style={{ marginRight: 10 }}>
                <AntSwitch />
              </div> */}
            {/* </Box> */}
          </Box>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              sx={{ minWidth: 100, minHeight: 35 }}
              disabled={
                !formik.dirty ||
                Object.keys(formik.errors).some(
                  (key) =>
                    key !== "validity" ||
                    formik.errors.validity === "Minimum is 30 days"
                )
              }

            >
              Update
            </Button>
          </Box>
        </form>
      </div>
    </div>
  );
};

export default InviteUpdateDrawer;
